import React, { useMemo } from 'react';
import { GenericSearchSelect, SelectableItem } from './GenericSearchSelect';
import { useAppSelector } from '../../../../../contexts/hooks';
import { useConfigReady } from '../../../../../hooks/useConfigReady';

// Interfaz específica para perfiles
export interface ProfileItem extends SelectableItem {
  id: number | string;
  code: string;
  name: string;
  group: string;
  isActive?: boolean;
  originalData?: any; // Datos originales del perfil con estudios
}

// Props del componente de perfiles
interface ProfilesSearchSelectProps {
  onProfileSelected: (profile: ProfileItem | null) => void;
  selectedProfiles?: ProfileItem[];
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  showGroups?: boolean;
  minSearchLength?: number;
  keepLabel?: boolean; // Nueva prop para mantener visible el elemento seleccionado
}

export const ProfilesSearchSelect: React.FC<ProfilesSearchSelectProps> = ({
  onProfileSelected,
  selectedProfiles = [],
  placeholder = 'Buscar perfiles...',
  label = 'Buscar perfiles',
  disabled = false,
  className = '',
  showGroups = false, // Los perfiles normalmente no se agrupan
  minSearchLength = 1, // Cambiar a 1 para ser consistente con estudios
  keepLabel = false, // Por defecto false para mantener comportamiento existente
}) => {
  const { pending: configPending } = useConfigReady();

  // Obtener perfiles de estudios (no perfiles de usuarios)
  const profilesRawData = useAppSelector(state => state.configuration.Data?.Perfiles || []);
  
  // Agrupar perfiles por Rpe_Pfe_Id como en el hook original
  const profilesData = useMemo(() => {
    return profilesRawData?.reduce((acumulador: any, actual) => {
      const { Rpe_Pfe_Id, SysI_Estudios, SysI_PerfilesEstudios, ...resto } = actual;
      const objetoAgrupado = acumulador.find((elem) => elem.Rpe_Pfe_Id === Rpe_Pfe_Id);

      if (objetoAgrupado) {
        objetoAgrupado.SysI_Estudios.push(SysI_Estudios);
      } else {
        acumulador.push({
          Rpe_Pfe_Id,
          SysI_PerfilesEstudios,
          SysI_Estudios: [SysI_Estudios],
        });
      }
      return acumulador;
    }, []) || [];
  }, [profilesRawData]);

  // Crear el dataSource para el componente genérico con lazy loading
  const dataSource = useMemo(() => ({
    load: async (options?: { skip?: number; take?: number; searchText?: string }): Promise<ProfileItem[]> => {
      const { skip = 0, take = 200, searchText = '' } = options || {};
      
      // Filtrar perfiles por texto de búsqueda si se proporciona
      let filteredProfiles = profilesData;
      if (searchText && searchText.length >= minSearchLength) {
        const search = searchText.toLowerCase();
        filteredProfiles = profilesData.filter(profile =>
          profile.SysI_PerfilesEstudios?.Pfe_Nombre?.toLowerCase().includes(search) ||
          profile.SysI_PerfilesEstudios?.Pfe_Codigo?.toLowerCase().includes(search)
        );
      } else if (searchText && searchText.length > 0 && searchText.length < minSearchLength) {
        // Si hay texto pero no cumple la longitud mínima, no mostrar nada
        filteredProfiles = [];
      }
      
      // Simular paginación
      const paginatedProfiles = filteredProfiles.slice(skip, skip + take);
      
      return paginatedProfiles.map(profile => ({
        id: profile.Rpe_Pfe_Id,
        code: profile.SysI_PerfilesEstudios?.Pfe_Codigo || `P${profile.Rpe_Pfe_Id}`,
        name: profile.SysI_PerfilesEstudios?.Pfe_Nombre || 'Perfil sin nombre',
        group: 'Perfiles',
        originalData: profile
      }));
    },
    
    getTotalCount: async (searchText?: string): Promise<number> => {
      if (!searchText || searchText.length < minSearchLength) {
        return profilesData.length;
      }
      
      const search = searchText.toLowerCase();
      const filteredProfiles = profilesData.filter(profile =>
        profile.SysI_PerfilesEstudios?.Pfe_Nombre?.toLowerCase().includes(search) ||
        profile.SysI_PerfilesEstudios?.Pfe_Codigo?.toLowerCase().includes(search)
      );
      
      return filteredProfiles.length;
    }
  }), [profilesData, minSearchLength]);

  // Manejar selección de perfil
  const handleItemSelected = (item: SelectableItem | null) => {
    if (!item) {
      // Si es null, el usuario limpió la selección
      onProfileSelected(null);
      return;
    }
    
    const profileItem: ProfileItem = {
      id: item.id,
      code: item.code || '',
      name: item.name,
      group: item.group || 'Perfiles',
      isActive: (item as any).isActive,
      originalData: (item as any).originalData, // Pasar los datos originales del perfil
    };
    onProfileSelected(profileItem);
  };

  // Los perfiles llegan con GetAllConfig, después de autenticar.
  const loadingProfiles = configPending && profilesRawData.length === 0;

  return (
    <GenericSearchSelect
      dataSource={dataSource}
      onItemSelected={handleItemSelected}
      selectedItems={selectedProfiles}
      placeholder={loadingProfiles ? 'Cargando perfiles...' : placeholder}
      label={label}
      disabled={disabled || loadingProfiles}
      className={className}
      searchFields={['name', 'code']}
      groupField="group"
      displayFields={{
        title: 'name',
        subtitle: 'code'
      }}
      showGroups={showGroups}
      minSearchLength={minSearchLength}
      keepLabel={keepLabel}
    />
  );
};
